// components/profile/context/ProfileFormContext.jsx
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { validateField, validateAllFields, calculateProfileProgress } from '../utils/validation';

const ProfileFormContext = createContext();

const getDefaultFormData = () => ({
  // Basic Info
  companyName: '',
  location: '',
  email: '',
  website: '',
  instagram: '',
  introParagraph: '',
  startingPrice: '',
  minimumTimeframe: '',

  // About You
  headerImage: '',
  firstName: '',
  surname: '',
  profilePhoto: '',
  brandIcon: '',
  title: '',
  bio: '',

  // Portfolio (canonical + legacy aliases)
  videoUrl: '',
  showcaseVideo: '',
  galleryPhotos: [],
  galleryImages: [],
  awards: [],
  tags: {},
  ethicalStatement: ''
});

const normalizeProfileData = (data = {}) => {
  const normalizedVideo = data.showcaseVideo || data.videoUrl || '';
  const normalizedGallery = Array.isArray(data.galleryImages)
    ? data.galleryImages
    : Array.isArray(data.galleryPhotos)
      ? data.galleryPhotos
      : data.galleryPhotos && typeof data.galleryPhotos === 'object'
        ? Object.values(data.galleryPhotos)
        : [];

  return {
    ...getDefaultFormData(),
    ...data,
    videoUrl: normalizedVideo,
    showcaseVideo: normalizedVideo,
    galleryPhotos: normalizedGallery,
    galleryImages: normalizedGallery
  };
};

export const useProfileFormContext = () => {
  const context = useContext(ProfileFormContext);
  if (!context) {
    throw new Error('useProfileFormContext must be used within ProfileFormProvider');
  }
  return context;
};

export const ProfileFormProvider = ({ children, initialData = {}, isActive = false }) => {
  const initialDataRef = useRef(normalizeProfileData(initialData));

  const [formData, setFormData] = useState({
    ...initialDataRef.current
  });

  // Form meta state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const normalizedInitial = normalizeProfileData(initialData);
    if (JSON.stringify(normalizedInitial) !== JSON.stringify(initialDataRef.current) && !isDirty) {
      setFormData(normalizedInitial);
      initialDataRef.current = normalizedInitial;
    }
  }, [initialData, isDirty]);

  // Update a single field
  const updateField = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Keep legacy/canonical aliases in sync until all consumers use one schema
      if (field === 'showcaseVideo') {
        newData.videoUrl = value;
      } else if (field === 'videoUrl') {
        newData.showcaseVideo = value;
      } else if (field === 'galleryImages') {
        newData.galleryPhotos = value;
      } else if (field === 'galleryPhotos') {
        newData.galleryImages = value;
      }
      
      if (JSON.stringify(newData) !== JSON.stringify(initialDataRef.current)) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
      
      return newData;
    });
    
    // Validate field with conditional logic based on isActive
    const error = validateField(field, value, formData, isActive);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors, formData, isActive]);

  // NEW METHOD: Update multiple fields at once (atomic update)
  const updateFields = useCallback((updates) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };
      
      if (JSON.stringify(newData) !== JSON.stringify(initialDataRef.current)) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
      
      return newData;
    });
    
    // Clear errors for updated fields
    const updatedFields = Object.keys(updates);
    if (updatedFields.some(field => errors[field])) {
      setErrors(prev => {
        const newErrors = { ...prev };
        updatedFields.forEach(field => {
          if (newErrors[field]) {
            delete newErrors[field];
          }
        });
        return newErrors;
      });
    }
  }, [errors]);

  // Mark field as touched
  const setFieldTouched = useCallback((field, isTouched = true) => {
    setTouched(prev => ({ ...prev, [field]: isTouched }));
  }, []);

  // Set field error
  const setFieldError = useCallback((field, error) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);
  
  // Set multiple field errors
  const setFieldErrors = useCallback((newErrors) => {
    setErrors(prev => ({ ...prev, ...newErrors }));
  }, []);

  // Validate all fields with conditional logic
  const validateForm = useCallback((touchAll = false) => {
    const validationErrors = validateAllFields(formData, isActive);
    setErrors(validationErrors);
    
    if (touchAll && Object.keys(validationErrors).length > 0) {
        const touchedFields = {};
        Object.keys(validationErrors).forEach(field => {
            touchedFields[field] = true;
        });
        setTouched(prev => ({ ...prev, ...touchedFields }));
    }
    
    return validationErrors;
  }, [formData, isActive]);

  // Reset form to a new or initial state
  const resetForm = useCallback((newData) => {
    const dataToResetTo = normalizeProfileData(newData || initialDataRef.current);
    setFormData(dataToResetTo);
    initialDataRef.current = dataToResetTo;
    setErrors({});
    setTouched({});
    setIsDirty(false);
    setIsSubmitting(false);
  }, []);

  // Get completion percentage
  const getCompletionPercentage = useCallback(() => {
    return calculateProfileProgress(formData);
  }, [formData]);

  const getChangedFields = useCallback(() => {
    const changedFields = {};

    Object.keys(formData).forEach((key) => {
      if (JSON.stringify(formData[key]) !== JSON.stringify(initialDataRef.current[key])) {
        changedFields[key] = formData[key];
      }
    });

    return changedFields;
  }, [formData]);

  const value = {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    isActive, // NEW: Expose isActive to consumers
    updateField,
    updateFields, // Export the new method
    setFieldTouched,
    setFieldError,
    setFieldErrors,
    validateForm,
    resetForm,
    setIsSubmitting,
    getCompletionPercentage,
    getChangedFields
  };

  return (
    <ProfileFormContext.Provider value={value}>
      {children}
    </ProfileFormContext.Provider>
  );
};
